import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, Globe, ChevronDown, X } from 'lucide-react';

const languages = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
  { code: 'es', label: 'Español', short: 'ES' },
];

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const currentLang = i18n.language || 'en';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('energiesse-lang', code);
    const lang = languages.find((l) => l.code === code);
    toast.success(t('language_changed'));
  };

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/projects', label: t('nav.projects') },
    { path: '/trust', label: t('nav.trust') },
    { path: '/partnerships', label: t('nav.partnerships') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-background transition-shadow duration-200 ${
        scrolled ? 'shadow-sm border-b border-border' : 'border-b border-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="container-site">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="header-logo"
          >
            <div className="flex items-center gap-1.5">
              {/* Energy icon mark */}
              <div className="w-7 h-7 rounded-md bg-[var(--energy-green)] flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M9 2L4 9h5l-2 5 7-8H9l2-4H9z" fill="white" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="font-heading font-semibold text-base tracking-tight text-foreground">
                ENERGIESSE
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${
                  isActive(link.path)
                    ? 'text-foreground bg-muted'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right cluster: Language + CTA */}
          <div className="flex items-center gap-2">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                data-testid="language-switcher"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden md:flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                >
                  <Globe className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">
                    {currentLang.toUpperCase()}
                  </span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${
                      currentLang === lang.code ? 'font-medium text-foreground' : 'text-muted-foreground'
                    }`}
                    data-testid={`lang-option-${lang.code}`}
                  >
                    <span className="font-mono-nums text-xs w-6">{lang.short}</span>
                    <span className="text-sm">{lang.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CTA Button - Desktop */}
            <Button
              size="sm"
              className="hidden md:flex bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary text-xs px-3 h-8"
              onClick={() => navigate('/contact')}
              data-testid="header-partnership-call-button"
            >
              {t('nav.cta')}
            </Button>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden h-8 w-8"
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="flex flex-col h-full">
                  {/* Mobile Header */}
                  <div className="flex items-center justify-between p-4 border-b border-border">
                    <div className="flex items-center gap-1.5">
                      <div className="w-6 h-6 rounded-md bg-[var(--energy-green)] flex items-center justify-center">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                          <path d="M9 2L4 9h5l-2 5 7-8H9l2-4H9z" fill="white" />
                        </svg>
                      </div>
                      <span className="font-heading font-semibold text-sm">ENERGIESSE</span>
                    </div>
                  </div>

                  {/* Mobile Nav */}
                  <nav className="flex flex-col p-4 gap-1 flex-1">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileOpen(false)}
                        className={`px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                          isActive(link.path)
                            ? 'text-foreground bg-muted'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </nav>

                  {/* Mobile Bottom */}
                  <div className="p-4 border-t border-border space-y-3">
                    {/* Language switcher mobile */}
                    <div className="flex items-center gap-2">
                      <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                      <div className="flex gap-1">
                        {languages.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => { changeLanguage(lang.code); }}
                            className={`px-2 py-1 text-xs font-mono-nums rounded ${
                              currentLang === lang.code
                                ? 'bg-[var(--energy-green)] text-white'
                                : 'bg-muted text-muted-foreground'
                            }`}
                            data-testid={`mobile-lang-${lang.code}`}
                          >
                            {lang.short}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button
                      className="w-full bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white text-sm"
                      onClick={() => { navigate('/contact'); setMobileOpen(false); }}
                      data-testid="mobile-cta-button"
                    >
                      {t('nav.cta')}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
